<template>
  <div class="ivr-flows-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="IVR Flows" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>IVR Flows</h1>
            <p class="subtitle">Build and manage your interactive voice response flows.</p>
          </div>
          <button class="create-btn" @click="showCreateModal = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            New Flow
          </button>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid" v-if="stats">
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(59,130,246,0.1); color: #3B82F6;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.total }}</div>
              <div class="stat-label">Total Calls</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(16,185,129,0.1); color: #10B981;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.completed }}</div>
              <div class="stat-label">Completed</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(139,92,246,0.1); color: #8B5CF6;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M6.18 15.64a2.18 2.18 0 0 0 2.18 2.18C9.61 19.49 11 21 12 22c1-1 2.39-2.51 3.64-4.18A2.18 2.18 0 0 0 17.82 15.64V8.36a2.18 2.18 0 0 0-2.18-2.18h-7.28a2.18 2.18 0 0 0-2.18 2.18v7.28z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.transferred }}</div>
              <div class="stat-label">Transferred</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(245,158,11,0.1); color: #F59E0B;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.abandoned }}</div>
              <div class="stat-label">Abandoned</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(20,184,166,0.1); color: #14B8A6;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.unread_vms }}<span class="stat-sub">/{{ stats.voicemails }}</span></div>
              <div class="stat-label">Unread VMs</div>
            </div>
          </div>
          <div class="stat-card">
            <div class="stat-icon" style="background: rgba(239,68,68,0.1); color: #EF4444;">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 20c-4.962 0-9-4.038-9-9s4.038-9 9-9 9 4.038 9 9-4.038 9-9 9zm0-14a1 1 0 0 0-1 1v5a1 1 0 0 0 2 0V8a1 1 0 0 0-1-1zm0 10a1.25 1.25 0 1 0 0-2.5A1.25 1.25 0 0 0 12 17z"/></svg>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stats.blacklisted }}</div>
              <div class="stat-label">Blacklisted</div>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          {{ error }}
        </div>

        <!-- Flows Table -->
        <div v-else class="table-card">
          <table class="data-table" v-if="flows.length">
            <thead>
              <tr>
                <th>Name</th>
                <th>Trigger DID</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="flow in flows" :key="flow.id">
                <td>
                  <div class="flow-name">{{ flow.name }}</div>
                  <div class="flow-desc" v-if="flow.description">{{ flow.description }}</div>
                </td>
                <td>
                  <span v-if="flow.trigger_number" class="did-badge">{{ flow.trigger_number }}</span>
                  <span v-else class="muted-text">Fallback</span>
                </td>
                <td>
                  <span class="status-badge" :class="'status-' + flow.status">{{ flow.status }}</span>
                </td>
                <td class="muted-text">{{ formatDate(flow.updated_at) }}</td>
                <td>
                  <div class="action-row">
                    <button class="action-btn" title="Edit in Builder" @click="openBuilder(flow.id)">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                    </button>
                    <button class="action-btn" title="Duplicate" @click="duplicate(flow.id)">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                    </button>
                    <button
                      class="action-btn"
                      :class="flow.status === 'active' ? 'action-btn-orange' : 'action-btn-green'"
                      :title="flow.status === 'active' ? 'Deactivate' : 'Activate'"
                      @click="toggleStatus(flow)"
                    >
                      <svg v-if="flow.status === 'active'" width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                      <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    </button>
                    <button class="action-btn action-btn-red" title="Delete" @click="confirmDelete(flow)">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-state">
            <div class="empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
            </div>
            <h3>No flows yet</h3>
            <p>Create your first IVR flow to get started.</p>
            <button class="create-btn" @click="showCreateModal = true">Create Flow</button>
          </div>
        </div>

        <!-- Create Flow Modal -->
        <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
          <div class="modal-container">
            <div class="modal-header">
              <h2>New IVR Flow</h2>
              <button class="modal-close" @click="showCreateModal = false">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <div class="form-group">
                <label class="form-label">Flow Name *</label>
                <input v-model="newFlow.name" type="text" class="form-input" placeholder="e.g. Main IVR" />
              </div>
              <div class="form-group">
                <label class="form-label">Description</label>
                <input v-model="newFlow.description" type="text" class="form-input" placeholder="Optional description" />
              </div>
              <div class="form-group">
                <label class="form-label">Trigger DID</label>
                <input v-model="newFlow.trigger_number" type="text" class="form-input" placeholder="e.g. 0225551234 (leave blank for fallback)" />
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="showCreateModal = false">Cancel</button>
              <button class="btn-primary" :disabled="creating || !newFlow.name" @click="createFlow">
                {{ creating ? 'Creating...' : 'Create & Edit' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Delete Confirm Modal -->
        <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
          <div class="modal-container modal-sm">
            <div class="modal-header">
              <h2>Delete Flow</h2>
              <button class="modal-close" @click="deleteTarget = null">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </button>
            </div>
            <div class="modal-body">
              <p class="confirm-text">Delete <strong>{{ deleteTarget.name }}</strong>? This action cannot be undone.</p>
            </div>
            <div class="modal-footer">
              <button class="btn-secondary" @click="deleteTarget = null">Cancel</button>
              <button class="btn-danger" :disabled="deleting" @click="doDelete">
                {{ deleting ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

export default {
  name: 'IVRFlowsView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      flows: [],
      stats: null,
      loading: true,
      error: null,
      showCreateModal: false,
      creating: false,
      newFlow: { name: '', description: '', trigger_number: '' },
      deleteTarget: null,
      deleting: false
    }
  },

  async mounted () {
    await this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const [flowsRes, statsRes] = await Promise.all([
          IVRService.getFlows(),
          IVRService.getStats()
        ])
        this.flows = flowsRes.flows || []
        this.stats = statsRes
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    openBuilder (id) {
      this.$router.push(`/ivr/flows/${id}/edit`)
    },

    async duplicate (id) {
      try {
        const res = await IVRService.duplicateFlow(id)
        await this.load()
        this.$router.push(`/ivr/flows/${res.id}/edit`)
      } catch (e) {
        alert('Error duplicating flow: ' + e.message)
      }
    },

    async toggleStatus (flow) {
      try {
        if (flow.status === 'active') {
          await IVRService.deactivateFlow(flow.id)
        } else {
          if (flow.trigger_number) {
            const conflict = this.flows.find(f =>
              f.id !== flow.id &&
              f.trigger_number === flow.trigger_number &&
              f.status === 'active'
            )
            if (conflict) {
              if (!confirm(`Another flow (${conflict.name}) is already active for DID ${flow.trigger_number}. Activate this one anyway?`)) return
            }
          }
          await IVRService.activateFlow(flow.id)
        }
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      }
    },

    confirmDelete (flow) {
      this.deleteTarget = flow
    },

    async doDelete () {
      this.deleting = true
      try {
        await IVRService.deleteFlow(this.deleteTarget.id)
        this.deleteTarget = null
        await this.load()
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.deleting = false
      }
    },

    async createFlow () {
      if (!this.newFlow.name) return
      this.creating = true
      try {
        const payload = {
          name: this.newFlow.name,
          description: this.newFlow.description || undefined,
          trigger_number: this.newFlow.trigger_number || undefined
        }
        const res = await IVRService.createFlow(payload)
        this.showCreateModal = false
        this.newFlow = { name: '', description: '', trigger_number: '' }
        this.$router.push(`/ivr/flows/${res.id}/edit`)
      } catch (e) {
        alert('Error: ' + e.message)
      } finally {
        this.creating = false
      }
    },

    formatDate (str) {
      if (!str) return '—'
      const d = new Date(str)
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.ivr-flows-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}
.create-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.create-btn:hover {
  background: #2563EB;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59,130,246,0.4);
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
}
.stat-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}
.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-info { flex: 1; min-width: 0; }
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
}
.stat-sub {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 400;
}
.stat-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 16px 20px;
  color: #DC2626;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  color: var(--text-secondary);
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  opacity: 0.5;
}
.empty-state h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}
.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* Table */
.table-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th {
  background: var(--bg-tertiary);
  padding: 12px 20px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--border-primary);
}
.data-table td {
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-primary);
  font-size: 14px;
  color: var(--text-primary);
}
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover { background: var(--bg-tertiary); }

.flow-name { font-weight: 500; color: var(--text-primary); }
.flow-desc { font-size: 12px; color: var(--text-secondary); margin-top: 2px; }
.did-badge {
  background: rgba(59,130,246,0.1);
  color: #3B82F6;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  font-family: monospace;
}
.muted-text { color: var(--text-secondary); font-size: 13px; }

.status-badge {
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}
.status-active { background: #DCFCE7; color: #15803D; }
.status-draft { background: #F3F4F6; color: #6B7280; }
.status-inactive { background: #FEE2E2; color: #DC2626; }

/* Action buttons */
.action-row { display: flex; gap: 6px; }
.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.action-btn:hover { background: var(--bg-tertiary); color: var(--text-primary); border-color: var(--border-secondary); }
.action-btn-red { color: #EF4444; }
.action-btn-red:hover { background: #FEF2F2; border-color: #FECACA; color: #DC2626; }
.action-btn-green { color: #10B981; }
.action-btn-green:hover { background: #ECFDF5; border-color: #A7F3D0; color: #059669; }
.action-btn-orange { color: #F59E0B; }
.action-btn-orange:hover { background: #FFFBEB; border-color: #FCD34D; color: #D97706; }

/* Buttons */
.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-primary:hover:not(:disabled) {
  background: #2563EB;
  transform: translateY(-1px);
}
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover { background: var(--bg-tertiary); }

.btn-danger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #EF4444;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.btn-danger:hover:not(:disabled) { background: #DC2626; }
.btn-danger:disabled { opacity: 0.6; cursor: not-allowed; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-sm { max-width: 400px; }
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
}
.modal-header h2 {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.modal-close:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.modal-body { padding: 20px 24px; }
.modal-footer {
  padding: 0 24px 24px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.form-group { margin-bottom: 16px; }
.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 15px;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.form-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.confirm-text {
  font-size: 15px;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.5;
}
</style>
